// Centralized data access layer that hits Supabase directly (PostgREST,
// Storage, and Edge Functions). Replaces the old FastAPI /api client.

import { supabase } from "@/lib/supabase";

// --------------------------- helpers ---------------------------
async function invoke(name, options = {}) {
  // supabase-js wraps the underlying fetch and rejects on non-2xx, but it
  // does NOT expose the response body in error.message. We need it to show
  // meaningful errors (rate limits, validation, etc.) to the end user.
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const base = process.env.REACT_APP_SUPABASE_URL;
  if (!base || !anon) throw new Error("Supabase config missing");

  const res = await fetch(`${base}/functions/v1/${name}`, {
    method: options.method || "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anon,
      Authorization: `Bearer ${token || anon}`,
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  let body = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON response */
  }
  if (!res.ok) {
    const msg =
      (body && (body.error || body.message)) ||
      `Request failed (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  return body;
}

function ensureNotError(error, fallback = "Request failed") {
  if (error) {
    const e = new Error(error.message || fallback);
    e.cause = error;
    throw e;
  }
}

// --------------------------- meta ------------------------------
export async function getDirectoryMeta() {
  const [c, s] = await Promise.all([
    supabase.from("surgeon_meta_cities").select("city"),
    supabase.from("surgeon_meta_specialties").select("specialty"),
  ]);
  ensureNotError(c.error);
  ensureNotError(s.error);
  return {
    cities: (c.data ?? []).map((r) => r.city).filter(Boolean),
    specialties: (s.data ?? []).map((r) => r.specialty).filter(Boolean),
  };
}

// --------------------------- leads -----------------------------
export async function captureLead(payload) {
  const webhookUrl = process.env.REACT_APP_LEAD_MAGNET_WEBHOOK_URL;
  if (webhookUrl && webhookUrl.startsWith("http")) {
    try {
      await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Failed to send lead to GHL:", err);
    }
  } else {
    console.log("Lead captured (simulated - missing GHL webhook URL):", payload);
  }
  return new Promise((resolve) => setTimeout(() => resolve({ ok: true }), 800));
}

// --------------------------- surgeons --------------------------
const SEARCH_STRIP_RE = /[,()%*]/g;
function sanitizeSearch(q) {
  if (!q) return null;
  const cleaned = String(q).replace(SEARCH_STRIP_RE, " ").trim().slice(0, 120);
  return cleaned || null;
}

// Tier weighting used by the directory sort (higher = appears first).
// "Top placement" (Fellow) and "Priority placement" (Active) are the
// concrete promises from the membership page that we enforce in code.
const TIER_RANK = { fellow: 3, active: 2, associate: 1 };
function tierRank(s) {
  if (s?.subscription_status !== "active") return 0;
  return TIER_RANK[s.subscription_tier] ?? 0;
}

export async function listSurgeons({ q, city, specialty, accepting } = {}) {
  let query = supabase
    .from("surgeons")
    .select("*")
    .eq("role", "surgeon")
    // Hide suspended members from the public directory.
    .not("subscription_status", "eq", "suspended");

  const safeQ = sanitizeSearch(q);
  if (safeQ) {
    query = query.or(
      `full_name.ilike.%${safeQ}%,practice_name.ilike.%${safeQ}%,primary_city.ilike.%${safeQ}%`,
    );
  }
  if (city) query = query.eq("primary_city", city);
  if (specialty) query = query.eq("primary_specialty", specialty);
  if (typeof accepting === "boolean") query = query.eq("accepting_new_patients", accepting);

  const { data, error } = await query.limit(200);
  ensureNotError(error);

  // Sort: tier rank (Fellow > Active > Associate > none) → rating → reviews → newest.
  return (data ?? []).slice().sort((a, b) => {
    const t = tierRank(b) - tierRank(a);
    if (t) return t;
    const r = (b.rating ?? 0) - (a.rating ?? 0);
    if (r) return r;
    const rc = (b.review_count ?? 0) - (a.review_count ?? 0);
    if (rc) return rc;
    return new Date(b.created_at) - new Date(a.created_at);
  });
}

export async function getSurgeon(id) {
  const { data, error } = await supabase.from("surgeons").select("*").eq("id", id).maybeSingle();
  ensureNotError(error);
  if (!data || data.subscription_status === "suspended") {
    const e = new Error("Surgeon not found");
    e.status = 404;
    throw e;
  }
  // Fire-and-forget profile-view tracking
  try {
    await supabase.rpc("increment_profile_view", { p_surgeon_id: id });
  } catch (_) {
    /* ignore */
  }
  return data;
}

export async function updateMyProfile(payload) {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("surgeons")
    .update(payload)
    .eq("id", u.user.id)
    .select()
    .single();
  ensureNotError(error);
  return data;
}

// --------------------------- gallery ---------------------------
export async function listMyGallery() {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("gallery")
    .select("*")
    .eq("surgeon_id", u.user.id)
    .eq("is_deleted", false)
    .order("created_at", { ascending: false });
  ensureNotError(error);
  return (data ?? []).map(withPublicUrl);
}

export async function listSurgeonGallery(surgeonId) {
  const { data, error } = await supabase
    .from("gallery")
    .select("*")
    .eq("surgeon_id", surgeonId)
    .eq("is_deleted", false)
    .order("created_at", { ascending: false });
  ensureNotError(error);
  return (data ?? []).map(withPublicUrl);
}

function withPublicUrl(row) {
  if (!row?.storage_path) return row;
  const { data } = supabase.storage.from("gallery").getPublicUrl(row.storage_path);
  return { ...row, url: data?.publicUrl };
}

const ALLOWED_IMAGE_MIME = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

export async function uploadGalleryImage(file, { procedure = "General", caption = "" } = {}) {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) throw new Error("Not authenticated");
  if (!ALLOWED_IMAGE_MIME.has(file.type)) {
    throw new Error("Only JPG, PNG, WEBP, GIF allowed");
  }
  if (file.size > 6 * 1024 * 1024) throw new Error("Image too large (max 6 MB)");

  const ext = file.name.split(".").pop().toLowerCase();
  const path = `${u.user.id}/${crypto.randomUUID()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from("gallery")
    .upload(path, file, { contentType: file.type, upsert: false });
  ensureNotError(upErr, "Upload failed");

  const { data, error } = await supabase
    .from("gallery")
    .insert({
      surgeon_id: u.user.id,
      storage_path: path,
      content_type: file.type,
      procedure,
      caption,
    })
    .select()
    .single();
  ensureNotError(error);
  return withPublicUrl(data);
}

export async function deleteGalleryItem(id) {
  // Find the row first to learn the storage path so we can remove the file
  const { data: row, error: fetchErr } = await supabase
    .from("gallery")
    .select("storage_path")
    .eq("id", id)
    .maybeSingle();
  ensureNotError(fetchErr);
  if (row?.storage_path) {
    await supabase.storage.from("gallery").remove([row.storage_path]);
  }
  const { error } = await supabase.from("gallery").delete().eq("id", id);
  ensureNotError(error);
  return { ok: true };
}

// --------------------------- news ------------------------------
// News articles are written by the n8n automation pipeline (news-ingest
// Edge Function) into the `news_articles` table, and read publicly here.
export async function listNews({ limit = 24 } = {}) {
  const { data, error } = await supabase
    .from("news_articles")
    .select("*")
    .order("published_at", { ascending: false })
    .limit(limit);
  ensureNotError(error);
  return (data || []).map(normalizeArticle);
}

export async function getNewsArticleBySlug(slug) {
  const { data, error } = await supabase
    .from("news_articles")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  ensureNotError(error);
  return data ? normalizeArticle(data) : null;
}

function normalizeArticle(row) {
  return {
    slug: row.slug,
    title: row.title,
    category: row.category || "Society",
    date: new Date(row.published_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    publishedAt: row.published_at,
    author: row.author || "FSCS Editorial Team",
    readTime: row.read_time || "4 min read",
    excerpt: row.excerpt || "",
    image:
      row.image_url ||
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1600&q=70",
    contentMarkdown: row.content_markdown || "",
    seoTitle: row.seo_title || row.title,
    seoDescription: row.seo_description || row.excerpt || "",
    canonicalUrl: row.canonical_url || null,
    source: row.source || "fcs",
  };
}

// --------------------------- headshot upload -------------------
// Uploads to the public `headshots` bucket and returns the public URL.
// The caller is responsible for persisting that URL on the surgeon row.
export async function uploadHeadshot(file) {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) throw new Error("Not authenticated");
  if (!ALLOWED_IMAGE_MIME.has(file.type)) {
    throw new Error("Only JPG, PNG, WEBP, GIF allowed");
  }
  if (file.size > 4 * 1024 * 1024) throw new Error("Image too large (max 4 MB)");

  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const path = `${u.user.id}/${crypto.randomUUID()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from("headshots")
    .upload(path, file, { contentType: file.type, upsert: false });
  ensureNotError(upErr, "Upload failed");

  const { data } = supabase.storage.from("headshots").getPublicUrl(path);
  return { url: data?.publicUrl, path };
}

// --------------------------- availability ----------------------
// Availability lives directly on the surgeons row (unavailable_dates[],
// accepting_new_patients, consult_note).
export async function getAvailability(surgeonId) {
  const { data, error } = await supabase
    .from("surgeons")
    .select("unavailable_dates, accepting_new_patients, consult_note")
    .eq("id", surgeonId)
    .maybeSingle();
  ensureNotError(error);
  return data || { unavailable_dates: [], accepting_new_patients: true, consult_note: "" };
}

export async function upsertMyAvailability(payload) {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) throw new Error("Not authenticated");
  const update = {};
  if (payload.unavailable_dates !== undefined) update.unavailable_dates = payload.unavailable_dates;
  if (payload.accepting_new_patients !== undefined) update.accepting_new_patients = payload.accepting_new_patients;
  if (payload.consult_note !== undefined) update.consult_note = payload.consult_note;
  const { data, error } = await supabase
    .from("surgeons")
    .update(update)
    .eq("id", u.user.id)
    .select("unavailable_dates, accepting_new_patients, consult_note")
    .single();
  ensureNotError(error);
  return data;
}

// --------------------------- inquiries -------------------------
export async function createInquiry(payload) {
  return invoke("inquiry-create", { body: payload });
}

export async function listMyInquiries() {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("inquiries")
    .select("*")
    .eq("surgeon_id", u.user.id)
    .order("created_at", { ascending: false });
  ensureNotError(error);
  return data ?? [];
}

export async function updateInquiryStatus(id, status) {
  const allowed = ["new", "contacted", "scheduled", "pending", "closed", "archived"];
  if (!allowed.includes(status)) throw new Error("Invalid status");
  const { error } = await supabase.from("inquiries").update({ status }).eq("id", id);
  ensureNotError(error);
  return { ok: true };
}

// --------------------------- reviews ---------------------------
export async function listReviews(surgeonId) {
  const { data, error } = await supabase
    .from("reviews")
    .select("*")
    .eq("surgeon_id", surgeonId)
    .eq("approved", true)
    .order("created_at", { ascending: false })
    .limit(50);
  // If the table doesn't exist yet, return empty gracefully
  if (error) return [];
  return data ?? [];
}

export async function createReview(payload) {
  const { surgeon_id, rating, reviewer_name, reviewer_email, procedure, body, type } = payload;
  const { data, error } = await supabase
    .from("reviews")
    .insert({
      surgeon_id,
      rating,
      reviewer_name,
      reviewer_email,
      procedure: procedure || null,
      body,
      type: type || "review",
      approved: false, // requires moderation
    })
    .select()
    .single();
  if (error) {
    // Graceful fallback: table may not exist yet
    console.warn("Review insert failed:", error.message);
    return { ok: true, fallback: true };
  }
  return data;
}

// --------------------------- analytics -------------------------
export async function getMyAnalytics() {
  const { data, error } = await supabase.rpc("analytics_me");
  ensureNotError(error);
  return data;
}

// --------------------------- auth ------------------------------
export async function getMe() {
  const { data: u, error } = await supabase.auth.getUser();
  if (error || !u?.user) return null;
  const { data: profile } = await supabase
    .from("surgeons")
    .select("*")
    .eq("id", u.user.id)
    .maybeSingle();
  return profile ?? null;
}

export async function loginEmail(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  ensureNotError(error, "Sign in failed");
  return data;
}

export async function registerSurgeon(payload) {
  // Calls our Edge Function, which creates the user, profile, and emails OTP
  return invoke("register-otp", { body: payload });
}

export async function verifyOtp(email, code, type = "signup") {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token: code,
    type,
  });
  if (error) {
    // Log full details to the console so we can see Supabase's exact response
    // when "Token expired or invalid" pops up.
    // eslint-disable-next-line no-console
    console.error("verifyOtp failed", { type, email, status: error.status, code: error.code, name: error.name, message: error.message });
    const e = new Error(error.message || "Verification failed");
    e.cause = error;
    e.status = error.status;
    throw e;
  }
  return data;
}

export async function resendOtp(email, password) {
  return invoke("resend-otp", { body: { email, password } });
}

export async function logout() {
  await supabase.auth.signOut();
}

export async function forgotPassword(email) {
  return invoke("forgot-password", { body: { email } });
}

export async function verifyPasswordResetOtp(email, code) {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token: code,
    type: "recovery",
  });
  ensureNotError(error, "Code is invalid or expired");
  return data;
}

export async function setNewPassword(newPassword) {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  ensureNotError(error, "Could not update password");
  return data;
}

// --------------------------- payments --------------------------
export async function createCheckoutSession({ tier, interval = "year", success_url, cancel_url }) {
  return invoke("stripe-checkout", {
    body: { tier, interval, success_url, cancel_url },
  });
}

export async function getCheckoutStatus(sessionId) {
  // supabase-js doesn't natively support GET on Edge Functions, so use fetch
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error("Not authenticated");
  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-status?session_id=${encodeURIComponent(sessionId)}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || "Status check failed");
  return body;
}

// Open Stripe's hosted Customer Portal so the surgeon can manage their
// subscription (update card, view invoices, cancel anytime).
export async function openBillingPortal(return_url) {
  return invoke("stripe-portal", { body: { return_url } });
}

// --------------------------- admin -----------------------------
// Admin queries - RLS gates these; callers without role='admin' get zero rows.
export async function adminListSurgeons() {
  const { data, error } = await supabase
    .from("surgeons")
    .select("*")
    .order("created_at", { ascending: false });
  ensureNotError(error);
  return data ?? [];
}

export async function adminListPayments() {
  const { data, error } = await supabase
    .from("payment_transactions")
    .select("*, surgeons:surgeon_id (full_name, email)")
    .order("created_at", { ascending: false })
    .limit(500);
  ensureNotError(error);
  return data ?? [];
}

export async function adminListInquiries() {
  const { data, error } = await supabase
    .from("inquiries")
    .select("*, surgeons:surgeon_id (full_name, email)")
    .order("created_at", { ascending: false })
    .limit(500);
  ensureNotError(error);
  return data ?? [];
}

export async function adminListNews() {
  const { data, error } = await supabase
    .from("news_articles")
    .select("*")
    .order("published_at", { ascending: false });
  ensureNotError(error);
  return (data || []).map(normalizeArticle);
}

export async function adminDeleteNews(id) {
  const { error } = await supabase.from("news_articles").delete().eq("id", id);
  ensureNotError(error);
  return { ok: true };
}

export async function adminUpdateSurgeon(id, payload) {
  const { data, error } = await supabase
    .from("surgeons")
    .update(payload)
    .eq("id", id)
    .select()
    .single();
  ensureNotError(error);
  return data;
}

export async function adminListReviews() {
  const { data, error } = await supabase
    .from("reviews")
    .select("*, surgeons:surgeon_id (full_name)")
    .order("created_at", { ascending: false })
    .limit(500);
  // Graceful: if table doesn't exist yet, return empty
  if (error) return [];
  return data ?? [];
}

export async function adminUpdateReview(id, payload) {
  const { data, error } = await supabase
    .from("reviews")
    .update(payload)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function adminDeleteReview(id) {
  const { error } = await supabase.from("reviews").delete().eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
}

